import { useState, useEffect, useMemo } from "react";
import "../Leads.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ArrowBigDownDash, ArrowBigRightDashIcon, Copy, CopyCheck, Plus, Trash2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { NOT_ADDED_LEADS as columns, getErrorMessageText } from "../../../utils/column_maps";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { formatTime, formatUSPhone, getOwnerColor, getMetricScoreClass, getPhoneTypeLabel, makeFpsLink, statuses, updateLocalStorageRolling } from "../../../utils/common";
import Pagination from "../../../components/Pagination";
import ConfirmModal from "../../../components/ConfirmModal";
import DMReasonConfirmModal from "../../../components/DMReasonConfirmModal";
import axiosInstance from "../../../api/axiosInstance";
import { useAuth } from "../../../context/AuthProvider";

const NotAddedLeads = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    address: "",
    owners: [
      {
        firstName: "",
        lastName: "",
        email: "",
        phones: "",
      },
    ],
  });

  const [copiedId, setCopiedId] = useState(null);

  const [fetchLoading, setFetchLoading] = useState(true);
  const leadsPerPage = 25;
  const [totalLeads, setTotalLeads] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);

  const [expandForm, setExpandForm] = useState(false);

  const [copiedAddresses, setCopiedAddresses] = useState([]);
  const [visitedLinks, setVisitedLinks] = useState([]);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const [leads, setLeads] = useState([]);

  // const indexOfLastLead = currentPage * leadsPerPage;
  // const indexOfFirstLead = indexOfLastLead - leadsPerPage;

  // const totalPages = Math.ceil((totalLeads || 0) / leadsPerPage);

  const permission = user?.permissions?.find((p) => ["not-added-leads", "not-added-leads-l1", "not-added-leads-l2"].includes(p.permission_name) && p.can_view);

  const permissionDelete = user?.permissions?.find((p) => ["not-added-leads", "not-added-leads-l1", "not-added-leads-l2"].includes(p.permission_name) && p.can_delete);

  let leadStatusParam = "";

  if (permission) {
    if (permission?.permission_name === "not-added-leads-l1") {
      leadStatusParam = "&lead_status=0";
    } else if (permission?.permission_name === "not-added-leads-l2") {
      leadStatusParam = "&lead_status=1";
    }
  }

  const filterColumns = useMemo(() => {
    if (permission?.permission_name === "not-added-leads-l2") {
      return columns;
    }

    return columns.filter((col) => col.key !== "reason_move");
  }, [permission]);

  const fetchLeads = async (page = 1, limit = leadsPerPage, showLoader = true) => {
    try {
      if (showLoader) setFetchLoading(true);
      setSearchParams({ page });
      const offset = (page - 1) * limit;
      const resp = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL}api/dealmachine_properties/leads?limit=${limit}&offset=${offset}&owner=true&fpPhones=true&status=not_contact_saved${leadStatusParam}&values=true&ai_data=true`);
      const data = await resp.data;
      if (data.success) {
        const sortedLeads = (data.leads || []).sort((a, b) => (b.id || 0) - (a.id || 0));
        // setLeads(sortedLeads);
        // setTotalLeads(data.total);
        setCurrentPage(page);

        setLeads((prevLeads) => {
          const prevMap = Object.fromEntries(prevLeads.map((l) => [l.id, l]));
          return sortedLeads.map((newLead) => {
            const prev = prevMap[newLead.id];
            return {
              ...newLead,
              // Preserve existing marketData and loading flag if they exist
              marketData: prev?.marketData ?? newLead.marketData,
              marketDataLoading: prev?.marketDataLoading ?? false,
            };
          });
        });

        setTotalLeads(data.total);

        const addressList = sortedLeads.map((l) => l.full_address).filter(Boolean);
        const zipList = [...new Set(sortedLeads.map((l) => l.zip).filter(Boolean))];

        if (showLoader && addressList.length && zipList.length) {
          fetchMarketDataBatch(addressList, zipList, showLoader);
        }
      }
    } catch (err) {
      console.error("Failed to fetch leads", err);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchMarketDataBatch = async (addressList = [], zipList = [], showLoader = true) => {
    if (!addressList?.length || !zipList?.length) return;

    // Only show loading indicators if explicitly requested
    if (showLoader) {
      setLeads((prevLeads) => prevLeads.map((lead) => (zipList.includes(lead.zip) ? { ...lead, marketDataLoading: true } : lead)));
    }

    try {
      const response = await axiosInstance.post(`/api/webhooks/market-data-batch`, {
        full_address: addressList,
      });

      if (response.data?.success) {
        let marketDataMap = {};
        if (Array.isArray(response.data.data)) {
          marketDataMap = Object.fromEntries(response.data.data.map((item) => [item.zip, item]));
        }

        setLeads((prevLeads) =>
          prevLeads.map((lead) => {
            const marketData = marketDataMap[lead.zip];
            // Always update marketData and clear loading for that lead
            return {
              ...lead,
              marketData: marketData ? marketData.normalizedMarket : lead.marketData,
              marketDataLoading: false,
            };
          }),
        );
      } else {
        // On API failure, clear loading for all affected leads
        setLeads((prevLeads) => prevLeads.map((lead) => (zipList.includes(lead.zip) ? { ...lead, marketDataLoading: false } : lead)));
      }
    } catch (error) {
      console.error("Failed to fetch market data", error);
      setLeads((prevLeads) => prevLeads.map((lead) => (zipList.includes(lead.zip) ? { ...lead, marketDataLoading: false } : lead)));
    }
  };

  useEffect(() => {
    fetchLeads(currentPage, leadsPerPage, true);

    const interval = setInterval(() => {
      fetchLeads(currentPage, leadsPerPage, false);
    }, 10000);

    return () => clearInterval(interval); // eslint-disable-next-line
  }, [currentPage]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [currentPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.address.trim()) {
      toast.error("Please enter a full address.");
      return;
    }

    // Validate owners
    for (let i = 0; i < formData.owners.length; i++) {
      const owner = formData.owners[i];
      const hasAnyField = owner.firstName.trim() || owner.lastName.trim() || owner.email.trim() || owner.phones.trim();

      if (hasAnyField) {
        if (!owner.firstName.trim()) {
          toast.error(`Owner ${i + 1}: First Name is required.`);
          return;
        }
        if (!owner.lastName.trim()) {
          toast.error(`Owner ${i + 1}: Last Name is required.`);
          return;
        }
        if (!owner.phones.trim()) {
          toast.error(`Owner ${i + 1}: At least one phone is required.`);
          return;
        }

        // Validate each phone line
        const phoneLines = owner.phones
          .split("\n")
          .map((p) => p.trim())
          .filter((p) => p !== "");

        for (let phone of phoneLines) {
          const digits = phone.replace(/\D/g, "");
          if (digits.length < 10) {
            toast.error(`Owner ${i + 1}: Phone "${phone}" is invalid (must contain at least 10 digits).`);
            return;
          }
        }
      }
    }

    try {
      setLoading(true);

      const payload = {
        full_address: formData.address,
        owners: formData.owners
          .filter((o) => o.firstName.trim() || o.lastName.trim() || o.email.trim() || o.phones.trim())
          .map((o) => {
            // normalize phones for payload only
            const phoneLines = o.phones
              .split("\n")
              .map((p) => p.trim())
              .filter((p) => p !== "");

            const normalizedPhones = phoneLines.map((phone) => {
              const digits = phone.replace(/\D/g, "");
              return digits.slice(-10); // keep last 10 digits
            });

            return {
              first_name: o.firstName.trim(),
              last_name: o.lastName.trim(),
              email: o.email.trim(),
              phones: normalizedPhones, // send normalized array
            };
          }),
      };

      //   console.log(payload);
      const resp = await fetch(`${process.env.REACT_APP_BASE_URL}api/dealmachine_properties/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await resp.json();

      if (data.success) {
        // toast.success(data.message || "Lead added successfully!");
        toast.success("Lead added successfully!");
        setFormData({
          address: "",
          owners: [{ firstName: "", lastName: "", email: "", phones: "" }],
        });
        fetchLeads();
      } else {
        toast.error(data.message || "Something went wrong.");
      }
    } catch (err) {
      toast.error("Failed to add lead. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMoveModal = (row) => {
    setSelectedRow(row);
    setMoveModalOpen(true);
  };

  const handleOpenDeleteModal = (row) => {
    setSelectedRow(row);
    setDeleteModalOpen(true);
  };

  const handleDeleteLead = async (identifier) => {
    try {
      const res = await axiosInstance.put(`${process.env.REACT_APP_BASE_URL}api/dealmachine_properties/dm-lead`, {
        lead_id: identifier,
        deleted: true,
      });

      if (!res?.data?.success) {
        throw new Error(res?.data?.message || "❌ Failed to Delete lead");
      }

      toast.success("Lead Deleted successfully.");

      fetchLeads();
    } catch (err) {
      console.error("Error deleting lead:", err);
      toast.error(err?.response?.data?.message || err.message || "Error Deleting Lead.");
    }
  };

  const totalTableWidth = filterColumns.reduce((sum, col) => sum + col.width, 0);

  const handleOwnerChange = (ownerIndex, field, value) => {
    setFormData((prev) => {
      const updatedOwners = [...prev.owners];
      updatedOwners[ownerIndex][field] = value;
      return { ...prev, owners: updatedOwners };
    });
  };

  // selectedData = {
  //   [leadId]: {
  //     owners: {
  //       [ownerId]: {
  //         owner,
  //         phones: [],
  //         email: null
  //       }
  //     }
  //   }
  // }

  const [selectedData, setSelectedData] = useState({});

  const toggleOwner = (leadId, owner) => {
    setSelectedData((prev) => {
      const prevLeadId = Object.keys(prev)[0];

      // Selecting owner from another lead → clear all
      if (prevLeadId && prevLeadId !== String(leadId)) {
        return {
          [leadId]: {
            owners: {
              [owner.id]: { owner, phones: [], email: null },
            },
          },
        };
      }

      const currentOwners = prev[leadId]?.owners || {};

      // Toggle owner
      if (currentOwners[owner.id]) {
        const updatedOwners = { ...currentOwners };
        delete updatedOwners[owner.id];

        return Object.keys(updatedOwners).length ? { [leadId]: { owners: updatedOwners } } : {};
      }

      return {
        [leadId]: {
          owners: {
            ...currentOwners,
            [owner.id]: { owner, phones: [], email: null },
          },
        },
      };
    });
  };

  const togglePhone = (leadId, owner, phoneObj) => {
    setSelectedData((prev) => {
      const currentOwner = prev[leadId]?.owners?.[owner.id];
      if (!currentOwner) return prev;

      const exists = currentOwner.phones.some((p) => p.phone === phoneObj.phone);

      const phones = exists ? currentOwner.phones.filter((p) => p.phone !== phoneObj.phone) : [...currentOwner.phones, phoneObj];

      return {
        [leadId]: {
          owners: {
            ...prev[leadId].owners,
            [owner.id]: { ...currentOwner, phones },
          },
        },
      };
    });
  };

  const toggleEmail = (leadId, owner, email) => {
    setSelectedData((prev) => {
      const currentOwner = prev[leadId]?.owners?.[owner.id];
      if (!currentOwner) return prev;

      return {
        [leadId]: {
          owners: {
            ...prev[leadId].owners,
            [owner.id]: {
              ...currentOwner,
              email: currentOwner.email === email ? null : email,
            },
          },
        },
      };
    });
  };

  const confirmSelection = ({ lead }) => {
    const owners = Object.values(selectedData[lead.id].owners).map((o) => ({
      firstName: o.owner.first_name,
      lastName: o.owner.last_name,
      email: o.email,
      phones: o.phones.map((p) => `${p.phone}${p.type ? " " + getPhoneTypeLabel(p.type) : ""}`).join("\n"),
    }));

    const selectedLead = {
      address: lead.full_address,
      owners,
    };

    const params = new URLSearchParams({
      lead: JSON.stringify(selectedLead),
      page: "1",
    });

    window.open(`/added-leads?${params.toString()}`, "_blank");
  };
  const addAddress = ({ lead }) => {
    const selectedLead = {
      address: lead.full_address,
      owners: [
        {
          firstName: "",
          lastName: "",
          email: "",
          phones: "",
        },
      ],
    };

    const params = new URLSearchParams({
      lead: JSON.stringify(selectedLead),
      page: "1",
    });

    window.open(`/added-leads?${params.toString()}`, "_blank");
  };

  useEffect(() => {
    const storedVisited = JSON.parse(localStorage.getItem("visitedLinks") || "[]");
    const storedCopied = JSON.parse(localStorage.getItem("copiedAddresses") || "[]");
    setVisitedLinks(storedVisited);
    setCopiedAddresses(storedCopied);
  }, []);

  const mapLeadData = (lead) => ({
    id: lead.id || "Not Found",
    created_at: formatTime(lead.created_at),
    lead_submitted: formatTime(lead.lead_submitted),
    status: statuses[lead.status] || lead.status,
    market_score: lead.marketData?.market_score != null ? <span className={getMetricScoreClass(lead.marketData.market_score >= 71 ? 2 : lead.marketData.market_score >= 41 ? 1 : 0)}>{lead.marketData.market_score} / 100</span> : "",
    full_address:
      (
        <div className="flex justify-between !space-x-4">
          <div className="flex item-center">
            <span>{lead.full_address}</span>
            <span className="!ml-2 flex !space-x-2">
              <a
                href={makeFpsLink(lead.address, lead.zip, lead.city, lead.state)}
                target="_blank"
                rel="noopener noreferrer"
                className={`!ml-2 text-nowrap ${visitedLinks.includes(lead.id) ? "!text-purple-800 text-medium" : "!text-blue-600 hover:!text-blue-800"}`}
                onClick={() => {
                  const updated = updateLocalStorageRolling("visitedLinks", lead.id);
                  setVisitedLinks(updated);
                }}
              >
                [ FPS ]
              </a>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(lead.full_address ?? "");
                  setCopiedId(lead.id);

                  const updated = updateLocalStorageRolling("copiedAddresses", lead.id);
                  setCopiedAddresses(updated);

                  setTimeout(() => setCopiedId(null), 2000);
                }}
                title={lead.full_address}
                className={`flex justify-center items-center !w-6 !h-6 rounded-full cursor-pointer ${copiedAddresses.includes(lead.id) ? "bg-green-600 text-white" : "bg-gray-200/70 hover:bg-gray-200/100 text-black"}`}
              >
                {copiedId === lead.id ? <CopyCheck className="!w-4 !h-4 text-black" /> : <Copy className="!w-4 !h-4" />}
              </button>
            </span>
          </div>
          <div>
            <button
              type="button"
              onClick={() => {
                addAddress({ lead });
              }}
              className="!px-3 !py-1 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:bg-gray-400 cursor-pointer text-nowrap"
            >
              Add Address
            </button>
          </div>
        </div>
      ) || "Not Found",
    dm_lead_id: lead.dm_lead_id || "Not Found",
    values: (
      <div className="flex flex-wrap gap-2">
        {lead.values?.length > 0 ? (
          lead.values.map((v) =>
            v.url ? (
              <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline text-sm">
                [{v.site}]
              </a>
            ) : null,
          )
        ) : (
          <span className="text-gray-400 text-sm"></span>
        )}
      </div>
    ),
    owners: lead.owner?.length ? (
      <div>
        {lead.owner.map((o, idx) => (
          <div key={o.id || idx} className="font-semibold" style={{ color: getOwnerColor(idx) }}>
            <span>
              {o.dm_owner ? "D" : "P"}-{o.name}
            </span>
          </div>
        ))}
      </div>
    ) : (
      "Not Found"
    ),
    dm_owners: lead.owner?.length ? (
      <div>
        {lead.owner.map((o, idx) =>
          o.dm_owner ? (
            <div key={o.id || idx} className="font-semibold" style={{ color: getOwnerColor(idx) }}>
              <span>{o.name}</span>
            </div>
          ) : null,
        )}
      </div>
    ) : (
      "Not Found"
    ),
    p_owners: lead.owner?.length ? (
      <div>
        {lead.owner.map((o, idx) =>
          !o.dm_owner ? (
            <div key={o.id || idx} className="font-semibold" style={{ color: getOwnerColor(idx) }}>
              <span>{o.name}</span>
            </div>
          ) : null,
        )}
      </div>
    ) : (
      "Not Found"
    ),
    fpPhones: lead.owner?.length ? (
      <>
        {lead.owner.map((owner, idx) => {
          const ownerPhones = lead.fpPhones?.filter((p) => p.owner_id === owner.id).map((p) => p) || [];
          const ownerEmails = [owner.email, owner.email_2, owner.email_3].filter(Boolean);

          const ownerSelected = !!selectedData[lead.id]?.owners?.[owner.id];

          return (
            <div key={idx} className="!mb-2 transition-all duration-200 ease-in-out">
              <label className="font-semibold flex item-center cursor-pointer" style={{ color: getOwnerColor(idx) }}>
                <input type="checkbox" className="!w-4 !h-4 accent-teal-600 !mr-2 cursor-pointer" checked={ownerSelected} onChange={() => toggleOwner(lead.id, owner)} />

                <span>{owner.name || "Unknown Owner"}</span>
              </label>

              {ownerPhones.length > 0 && (
                <div className="text-gray-700">
                  {ownerPhones.map((phone, index) => (
                    <label key={index} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="!w-4 !h-4 accent-teal-600 cursor-pointer" checked={ownerSelected && selectedData[lead.id]?.owners?.[owner.id]?.phones?.some((p) => p.phone === phone.phone)} onChange={() => togglePhone(lead.id, owner, phone)} />
                      <span>
                        {formatUSPhone(phone.phone)}
                        {phone.type && (
                          <>
                            {" - "}
                            {getPhoneTypeLabel(phone.type)}
                          </>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}

              {ownerEmails.length > 0 && (
                <div className="text-gray-500">
                  {ownerEmails.map((email, index) => (
                    <label key={index} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="!w-4 !h-4 accent-teal-600 cursor-pointer" checked={ownerSelected && selectedData[lead.id]?.owners?.[owner.id]?.email === email} onChange={() => toggleEmail(lead.id, owner, email)} />
                      <span>{email}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {!!selectedData[lead.id]?.owners && (
          <div className="!pl-10">
            <button onClick={() => confirmSelection({ lead })} className="!px-3 !py-1 bg-teal-600 text-white text-sm ! rounded hover:bg-teal-700 disabled:bg-gray-400 cursor-pointer transition-all duration-200 ease-in-out">
              Add Lead
            </button>
          </div>
        )}
      </>
    ) : (
      "Not Found"
    ),
    error_message: lead.is_loading ? <div>Loading ...</div> : <div>{getErrorMessageText(lead.error_message)}</div>,
    reason_move: <div>{lead.reason_move}</div>,
    within_city_limits: lead?.ai_data?.within_city_limits !== null && lead?.ai_data?.within_city_limits !== undefined ? <span>{lead?.ai_data.within_city_limits ? "Yes" : "No"}</span> : "",
    metro_details:
      lead?.ai_data?.nearest_metro || lead?.ai_data?.metro_distance_miles || lead?.ai_data?.metro_distance_minutes || lead?.ai_data?.metro_population ? (
        <div>
          {lead?.ai_data?.nearest_metro && (
            <div>
              <strong>Metro:</strong> {lead?.ai_data.nearest_metro}
            </div>
          )}
          {lead?.ai_data?.metro_distance_miles != null && (
            <div>
              <strong>Distance:</strong> {lead?.ai_data.metro_distance_miles} mi
            </div>
          )}
          {lead?.ai_data?.metro_distance_minutes != null && (
            <div>
              <strong>Time:</strong> {lead?.ai_data.metro_distance_minutes} min
            </div>
          )}
          {lead?.ai_data?.metro_population != null && (
            <div>
              <strong>Population:</strong> {lead?.ai_data.metro_population.toLocaleString()}
            </div>
          )}
        </div>
      ) : (
        ""
      ),
    nearby_density: lead?.ai_data?.nearby_density != null ? <span>{lead?.ai_data.nearby_density.toLocaleString()}</span> : "",
    area_type: lead?.ai_data?.area_type ? <span style={{ textTransform: "capitalize" }}>{lead?.ai_data.area_type}</span> : "",
  });

  return (
    <>
      <div>
        <div className="input_fields_container">
          <form className="w-full grid md:grid-cols-3 gap-4" onSubmit={handleSubmit}>
            <div className="!flex !items-center !justify-center md:col-span-3">
              <div className="!w-full">
                <input type="text" name="address" className="input_field SP_input_field focus:!outline-none !w-full" placeholder="Address..." value={formData.address} onChange={(e) => handleChange("address", e.target.value)} />
              </div>
              <div className="!px-2 !pb-2">
                <ArrowBigDownDash size={24} className={`transition-transform duration-300 cursor-pointer flex justify-center item-center hover:text-blue-800 ${expandForm ? "rotate-180" : "rotate-0"}`} onClick={() => setExpandForm((prev) => !prev)} />
              </div>
            </div>
            <div className="flex justify-center item-start w-full md:col-span-3 ">
              <button
                type="button"
                className={`overflow-hidden bg-[#008080] text-white rounded hover:bg-[#009999] cursor-pointer shadow transition-all duration-500 ${expandForm ? "scale-100 !mt-3 !mr-3 !p-1 h-min " : "scale-0 h-0 w-0"}`}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    owners: [...prev.owners, { firstName: "", lastName: "", email: "", phones: "" }],
                  }))
                }
              >
                <Plus size={20} />
              </button>
              <div className={`w-full transition-all duration-500 !pr-5 ${expandForm ? "max-h-[300px] opacity-100 mt-4 overflow-y-auto " : "max-h-0 opacity-0 overflow-hidden "}`}>
                {formData.owners.map((owner, i) => (
                  <div key={i} className="!flex !items-center !justify-center !mb-2">
                    <div className="!w-full rounded-xl !pr-5 flex item-start">
                      <div className="w-full">
                        <div className="flex flex-wrap flex-col md:flex-row gap-2 items-center mb-2">
                          <input type="text" placeholder="First Name" className="input_field flex-1 outline-none" value={owner.firstName} onChange={(e) => handleOwnerChange(i, "firstName", e.target.value)} />
                          <input type="text" placeholder="Last Name" className="input_field flex-1 outline-none" value={owner.lastName} onChange={(e) => handleOwnerChange(i, "lastName", e.target.value)} />
                          <input type="email" placeholder="Email" className="input_field flex-1 outline-none" value={owner.email} onChange={(e) => handleOwnerChange(i, "email", e.target.value)} />
                        </div>
                        <textarea name="phones" id="phones" placeholder="Phones..." rows={5} className="input_field flex-1 bg-white outline-none" value={owner.phones} onChange={(e) => handleOwnerChange(i, "phones", e.target.value)}></textarea>
                      </div>
                      {formData.owners.length > 1 && (
                        <button
                          type="button"
                          className="h-min !mt-3 !ml-3 bg-red-700 text-white !p-1 rounded hover:bg-red-800 hover:text-white cursor-pointer shadow"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              owners: prev.owners.filter((_, idx) => idx !== i),
                            }))
                          }
                        >
                          <Trash2 size={20} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="w-full md:col-span-3">
              <button className="w-full !max-w-xs text-white cursor-pointer text-xl !px-6 !py-2 rounded-sm !bg-[#008080] hover:!bg-[#007070]" type="submit" disabled={loading}>
                {loading ? "Adding..." : "Add Lead"}
              </button>
            </div>

            {error && <div style={{ color: "red", gridColumn: "1 / -1" }}>{error}</div>}
          </form>
        </div>

        <div className="mt-6 !w-full !overflow-x-auto">
          <table className="!border !border-gray-300 !table-auto min-w-[97%]" style={{ width: `${totalTableWidth}px` }}>
            <thead>
              <tr className="!bg-gray-200">
                {permission?.permission_name === "not-added-leads-l1" && <th className="!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap !w-8 !min-w-8"></th>}
                {(permissionDelete?.permission_name === "not-added-leads-l2" || permissionDelete?.permission_name === "not-added-leads") && <th className="!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap !w-8 !min-w-8"></th>}
                {filterColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`!border !border-gray-300 !px-4 !py-2`}
                    style={{
                      minWidth: `${col.width}px`,
                      width: `${col.width}px`,
                      textAlign: col.headerAlign,
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!fetchLoading ? (
                <>
                  {leads.length > 0 ? (
                    leads.map((lead, idx) => {
                      const row = mapLeadData(lead);
                      return (
                        <tr key={lead.id || idx} className="!hover:bg-gray-100 transition-all duration-200 ease-in-out">
                          {permission?.permission_name === "not-added-leads-l1" && (
                            <td className="!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap !w-4">
                              <button onClick={() => handleOpenMoveModal(row)} className="!text-blue-600 hover:!text-blue-800 !flex !items-center !justify-center !px-1 !py-2 cursor-pointer" title="Move Lead">
                                <ArrowBigRightDashIcon size={18} />
                              </button>
                            </td>
                          )}
                          {(permissionDelete?.permission_name === "not-added-leads-l2" || permissionDelete?.permission_name === "not-added-leads") && (
                            <td className="!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap !w-4">
                              <button onClick={() => handleOpenDeleteModal(row)} className="!text-red-600 hover:!text-red-800 !flex !items-center !justify-center !px-1 !py-2 cursor-pointer" title="Delete Lead">
                                <Trash2 size={18} />
                              </button>
                            </td>
                          )}
                          {filterColumns.map((col) => (
                            <td
                              key={col.key}
                              className={`truncate !border !border-gray-300 !px-4 !py-2 text-sm transition-all duration-200 ease-in-out
                            ${row[col.key] === "Not Found" ? "text-red-500" : /(CONFIRMED)/i.test((row[col.key] ?? "").toString()) ? "text-green-500" : /(NO-ANSWER)/i.test((row[col.key] ?? "").toString()) ? "text-yellow-500" : "text-black"}`}
                              style={{
                                minWidth: `${col.width}px`,
                                width: `${col.width}px`,
                                textAlign: col.cellAlign,
                                whiteSpace: "pre-line",
                              }}
                            >
                              {row[col.key]}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={filterColumns.length} className="!border !border-gray-300 !p-4 !text-left !text-gray-500">
                        <div className="w-full max-w-screen text-center">No leads found.</div>
                      </td>
                    </tr>
                  )}
                </>
              ) : (
                <>
                  <tr>
                    <td colSpan={filterColumns.length} className="!border !border-gray-300 !p-4 !text-left !text-gray-500">
                      <div className="w-full max-w-screen text-center">
                        <LoadingSpinner size={30} textSize={20} />
                      </div>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
        <Pagination totalItems={totalLeads} currentPage={currentPage} itemsPerPage={leadsPerPage} onPageChange={(page) => fetchLeads(page)} />

        <DMReasonConfirmModal isOpen={moveModalOpen} setMoveModalOpen={setMoveModalOpen} onCancel={() => setMoveModalOpen(false)} selectedRow={selectedRow} identifier={selectedRow?.id} fetchData={fetchLeads} />

        {/* Delete Confirm Modal */}
        <ConfirmModal
          title="Confirm Delete"
          message={`Are you sure you want to delete this lead?`}
          isOpen={deleteModalOpen}
          onConfirmTitle="Delete"
          onConfirm={() => {
            if (selectedRow) {
              handleDeleteLead(selectedRow.id, 1);
              // console.log("lead deleted", selectedRow.id, 1);
            }
            setDeleteModalOpen(false);
          }}
          onCancelTitle="Cancel"
          onCancel={() => setDeleteModalOpen(false)}
        />
      </div>
    </>
  );
};

export default NotAddedLeads;
